<template>
  <div class="flex flex-col flex-1 min-h-0">
  <!-- Messages Container -->
  <div 
    ref="messagesContainer" 
    class="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-100"
    style="min-height: 0;"
  >
    <!-- Empty State -->
    <div v-if="messages.length === 0" class="flex flex-col items-center py-4">
      <HelpBotAvatar class="mb-3" :url="settings.AvatarUrl" :name="settings.BotName" :size="64" />
      <p class="font-medium text-lg text-primary text-center" :style="accentText">I'm {{ settings.BotName }}. How can I help?</p>

      <div class="help-start-grid">
        <section v-for="example in examples" :key="example.id" class="help-start-card">
          <p class="text-sm font-semibold text-gray-800">{{ example.title }}</p>
          <p class="text-xs text-gray-500 mt-1">{{ example.description }}</p>
          <button
            v-for="prompt in example.prompts"
            :key="prompt"
            type="button"
            class="help-example"
            :disabled="!conversationPath || isWaiting"
            @click="askExample(prompt)"
          >
            <span class="flex-grow">{{ prompt }}</span>
            <font-awesome-icon icon="arrow-right" class="text-primary ml-3 flex-shrink-0"></font-awesome-icon>
          </button>
        </section>
      </div>
    </div>

    <!-- Message List -->
    <div
      v-for="message in messages"
      :key="message.id"
      :data-help-message="message.id"
      class="flex flex-col"
      :class="message.Role === 'user' ? 'items-end' : 'items-start'"
    >
      <div class="flex flex-row items-end gap-2 w-full" :class="message.Role === 'user' ? 'justify-end' : 'justify-start'">
        <HelpBotAvatar
          v-if="message.Role !== 'user'"
          :url="settings.AvatarUrl"
          :name="settings.BotName"
          :size="28"
        />
        <div
          class="help-bubble text-sm"
          :class="message.Role === 'user' ? ['help-bubble-user', { 'help-bubble-accent': userBubbleStyle }] : 'help-bubble-bot'"
          :style="message.Role === 'user' ? userBubbleStyle : null"
        >
          <p v-if="message.Role !== 'user'" class="text-xs font-medium text-primary mb-1" :style="accentText">{{ settings.BotName }}</p>
          <div v-if="showReasoning(message)" class="help-reasoning">
            <button
              type="button"
              class="help-reasoning-toggle"
              :aria-expanded="openReasoning[message.id] ? 'true' : 'false'"
              @click="toggleReasoning(message.id)"
            >
              <span :class="{ 'help-reasoning-pulse': message.Pending && !openReasoning[message.id] }">
                {{ message.Pending ? 'Thinking…' : 'Reasoning' }}
              </span>
            </button>
            <p v-if="message.Pending && !openReasoning[message.id]" class="help-reasoning-activity help-reasoning-pulse">Still working</p>
            <pre v-if="openReasoning[message.id] && message.Reasoning" class="help-reasoning-body">{{ message.Reasoning }}</pre>
          </div>
          <HelpMessageBody v-if="message.Message || message.Parts?.length" :message="message" :files="conversationFiles" />
          <div v-if="message.Role === 'user' && message.FileIds?.length" class="flex flex-wrap gap-1 mt-2">
            <HelpFileChip
              v-for="fileId in message.FileIds"
              :key="fileId"
              :label="conversationFiles[fileId]?.Name || 'File'"
              :path="conversationFiles[fileId]?.Path || ''"
              :status="conversationFiles[fileId]?.Status || 'ready'"
            />
          </div>

          <div v-if="message.Widget && message.Role === 'bot' && !isFullWidthWidget(message.Widget.widgetID)" class="mt-2">
            <component
              v-if="getWidgetRenderer(message.Widget.widgetID)"
              :is="getWidgetRenderer(message.Widget.widgetID)"
              :widgetData="message.Widget.widgetData"
              :messageId="message.id"
              :conversationId="props.conversationId"
            />
          </div>
        </div>
      </div>
      <div v-if="message.Widget && message.Role === 'bot' && isFullWidthWidget(message.Widget.widgetID)" class="w-full pl-9 mt-2">
        <component
          :is="getWidgetRenderer(message.Widget.widgetID)"
          :widgetData="message.Widget.widgetData"
          :messageId="message.id"
          :conversationId="props.conversationId"
          :files="conversationFiles"
        />
      </div>
      <div
        v-if="message.id === offered.messageId && offered.options.length"
        class="flex flex-wrap gap-1 pl-9 mt-2"
        role="group"
        aria-label="Suggested choices"
      >
        <button
          v-for="option in offered.options"
          :key="option.id"
          type="button"
          class="help-option"
          :disabled="!conversationPath || isWaiting || filesBlocking"
          @click="chooseOption(option)"
        >
          <span class="help-option-label text-primary">{{ option.label }}</span>
        </button>
      </div>
      <span class="text-[11px] text-gray-400 mt-1 px-1" :class="{ 'ml-9': message.Role !== 'user' }">{{ formatTime(message.Time) }}</span>
    </div>

    <!-- Loading Indicator -->
    <div v-if="isWaiting && !pendingReply" class="flex flex-row items-end gap-2 justify-start">
      <HelpBotAvatar :url="settings.AvatarUrl" :name="settings.BotName" :size="28" />
      <div class="help-bubble help-bubble-bot">
        <p class="text-xs font-medium text-primary" :style="accentText">{{ settings.BotName }}</p>
        <div class="help-typing lds-ellipsis" :aria-label="`${settings.BotName} is typing`"><div></div><div></div><div></div><div></div></div>
      </div>
    </div>
  </div>

  <!-- Composer -->
  <div class="border-t border-gray-100 flex-shrink-0 px-2 pb-2">
    <div v-if="drafts.length || fileError" class="flex flex-wrap items-center gap-1 px-1 pt-2">
      <span v-for="draft in drafts" :key="draft.fileId" class="inline-flex items-center gap-1">
        <HelpFileChip :label="draft.name" :path="draft.path" :status="draft.status" :error="draft.error" />
        <button type="button" class="cancel !m-0 !px-2" :aria-label="`Remove ${draft.name}`" @click="removeDraft(draft)">
          <font-awesome-icon icon="times" />
        </button>
      </span>
      <p v-if="fileError" class="text-xs text-red-600 w-full">{{ fileError }}</p>
    </div>
    <Shell label="">
      <template v-slot:prefix>
        <button
          type="button"
          class="cancel !m-0 !px-2 flex-shrink-0"
          aria-label="Attach files"
          v-tooltip="'Attach files'"
          :disabled="!conversationPath || isWaiting"
          @click="fileInput?.click()"
        >
          <font-awesome-icon icon="paperclip" />
        </button>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          multiple
          :accept="FILE_ACCEPT"
          @change="onFiles"
        />
      </template>
      <template v-slot:comp>
        <div class="flex-1 min-w-0">
          <EntityText
            v-model="newMessage"
            bare
            compact
            submit-on-enter
            suggestions-above
            name="helpMessage"
            label="Message"
            placeholder="Ask a question, or type a number to tag a record"
            :pill-icon="pillIcon"
            @submit="sendMessage"
          />
        </div>
      </template>
      <template v-slot:suffix>
        <button
          type="button"
          class="primary !m-0 flex-shrink-0"
          :disabled="!hasMessage || !conversationPath || filesBlocking"
          v-tooltip="'Send (Enter)'"
          @click="sendMessage"
        >
          <font-awesome-icon icon="paper-plane" class="mr-2"></font-awesome-icon>
          Send
        </button>
      </template>
    </Shell>
  </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import firebase from 'firebase/app';
import 'firebase/functions';
import 'firebase/storage';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { appConfig, db } from '../../../db.js';
import Shell from '@resources/web-components/Inputs/Shell.vue';
import EntityText from '@resources/web-components/Inputs/EntityText.vue';
import { getWidgetRenderer, isFullWidthWidget } from '../../../help/widgets/registry.js';
import defaultAvatar from '../../../assets/helpchatdefault.png';
import HelpBotAvatar from './HelpBotAvatar.vue';
import HelpFileChip from './HelpFileChip.vue';
import HelpMessageBody from './HelpMessageBody.vue';
import { FILE_ACCEPT, MAX_FILES_PER_UPLOAD, validateClientFile } from './helpChatFiles.mjs';
import { FALLBACK_ICON, appIconForRoute } from './appIcon.mjs';
import { resolveSettings } from './helpChatSettings.mjs';
import { entityRoute, messageOptions, plainText } from './helpMessage.mjs';
import { helpChatRoute, requestHelpReply } from './requestHelpReply.js';

const props = defineProps({
  conversationId: {
    type: String,
    default: null,
  },
});

const store = useStore();
const router = useRouter();
const messagesContainer = ref(null);
const fileInput = ref(null);
const newMessage = ref('');
const isWaiting = ref(false);
const openReasoning = ref({});
const drafts = ref([]);
const fileError = ref('');
const hasMessage = computed(() => plainText(newMessage.value).trim().length > 0);
const filesBlocking = computed(() => drafts.value.some((draft) => draft.status !== 'ready'));
let alive = true;
onUnmounted(() => {
  alive = false;
});

const settings = computed(() => resolveSettings(store.state.helpChatSettings));
const accentText = computed(() => (settings.value.AccentColor ? { color: settings.value.AccentColor } : null));
const userBubbleStyle = computed(() => (settings.value.AccentColor ? { background: settings.value.AccentColor } : null));

function notifyReply(message) {
  if (!settings.value.Notifications || !document.hidden) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const notification = new Notification(`${settings.value.BotName} replied`, {
      body: plainText(message.Message || '').trim().slice(0, 140),
      icon: settings.value.AvatarUrl || defaultAvatar,
      tag: 'stc-help-chat',
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (error) {
    console.warn('[HelpChat] Could not show a notification', error);
  }
}

function pillIcon(segment) {
  const name = appIconForRoute(router, entityRoute(segment.entityType, segment.entityId));
  const svg = (icon({ prefix: 'fas', iconName: name }) || icon({ prefix: 'fas', iconName: FALLBACK_ICON }))?.node[0];
  svg?.classList.add('text-primary');
  return svg || null;
}

const examples = [
  {
    id: 'app',
    title: 'How to use the app',
    description: 'Steps for screens, fields, and actions in this app.',
    prompts: [
      'How do I add a user?',
      'How do I link a drawing to a design change?',
      'Where do I find the signatory matrix?',
    ],
  },
  {
    id: 'procedure',
    title: 'Company procedures',
    description: 'Handbooks and company procedures that have been indexed.',
    prompts: [
      'What is Handbook 2 and its purpose?',
      'What is the process for raising a concession?',
      'Who approves a design change?',
    ],
  },
  {
    id: 'records',
    title: 'Records',
    description: 'Counts, lists, and the status of records. Type a record number to tag it, then ask about that record.',
    prompts: [
      'Are there any open concessions?',
      'Are there any open non-conformities?',
      'Which job cards are not signed off by the technical secretary, and who is the lead engineer?',
    ],
  },
  {
    id: 'charts',
    title: 'Charts',
    description: 'Line, bar, area, and pie charts of record counts. Each chart is saved to your files.',
    prompts: [
      'Bar chart of DQNs by year created.',
      'Pie chart of concessions by aircraft type.',
      'Line chart of DQNs, DOIs, service bulletins, and concessions by year created.',
    ],
  },
  {
    id: 'tables',
    title: 'Tables',
    description: 'A table of records, or a count of records by a field such as reason.',
    prompts: [
      'Table of open DQNs created since January 2026.',
      'Table of open concessions with their aircraft type.',
      'Count of DQNs by reason, as a table.',
    ],
  },
  {
    id: 'figures',
    title: 'Headline figures',
    description: 'One or a few numbers, not a chart.',
    prompts: [
      'How many DQNs are there, as a headline figure?',
      'How many open concessions are there?',
      'How many job cards were signed off by the engineer in 2025?',
    ],
  },
  {
    id: 'costs',
    title: 'Costs',
    description: 'Totals and averages of purchase order prices. Needs the Projects (Commercials) permission.',
    prompts: [
      'Bar chart of total purchase order price by customer.',
      'Table of average purchase order price by year.',
      'A report on purchase order costs since January 2026.',
    ],
  },
  {
    id: 'timelines',
    title: 'Timelines',
    description: 'Records in date order.',
    prompts: [
      'Timeline of design changes completed in 2025.',
      'Timeline of job cards signed off by the engineer between October and December 2025.',
      'Timeline of concessions created in 2025.',
    ],
  },
  {
    id: 'reports',
    title: 'Reports',
    description: 'Several of those views in one saved report.',
    prompts: [
      'Build a 2025 report on concessions and service bulletins with open, cancelled, and completed totals, a monthly trend, and a table of the ones still open.',
      'A report of DQNs by year and by reason.',
      'A 2025 report of job cards signed off by the engineer, with a headline total and a monthly chart.',
    ],
  },
  {
    id: 'links',
    title: 'Links from a record',
    description: 'A diagram of one record and the records it points at. Name the record, or type its number to tag it.',
    prompts: [
      'Show how DQN-2207 links to other records.',
      'What does drawing S21.DWG-100 link to?',
      'Which documents does concession S21.CON-10 point at?',
    ],
  },
  {
    id: 'design-change',
    title: "What's on a design change",
    description: 'The records hanging off a design change, one step away.',
    prompts: [
      'Diagram the DQNs, drawings, and concessions on design change 23-13-1077.',
      'Show the documents and DOIs linked to design change 23-13-1077.',
      'What service bulletins sit on design change 23-13-1077?',
    ],
  },
  {
    id: 'affects',
    title: 'What a record affects',
    description: 'Start from a drawing or document and see the design changes it is tied to.',
    prompts: [
      'If drawing S21.DWG-100 is revised, which design changes does it link to?',
      'Which design changes are tied to document S21.TEC-0274?',
      'Show the design changes linked from drawing S21.DWG-100.',
    ],
  },
  {
    id: 'files',
    title: 'Files',
    description: 'Attach a file with the paperclip, then ask about it. Saved reports are on the Files tab.',
    prompts: [
      'Summarise the file I attach.',
      'What actions does the attached document ask for?',
      'Compare the two files I attach.',
    ],
  },
];

// Get current user ID
const userId = computed(() => {
  const id = store.getters.userID || store.state.user?.Basic?.key;
  return id && id !== 'Unknown' ? id : null;
});

// Get messages from Vuex store
const conversationPath = computed(() => {
  if (!userId.value || !props.conversationId) return null;
  return `HelpChats/${userId.value}/Conversations/${props.conversationId}`;
});

const conversationFiles = computed(() => store.state.helpChatList?.[props.conversationId]?.Files || {});

const messages = computed(() => {
  const conversation = store.state.helpChatList?.[props.conversationId];
  if (!conversation?.Messages) return [];

  return conversation.Messages.map((msg, index) => ({
    ...msg,
    id: `${props.conversationId}-${index}`
  }));
});

const pendingReply = computed(() => {
  const last = messages.value[messages.value.length - 1];
  return Boolean(last && last.Role === 'bot' && last.Pending);
});

/** Choices offered by the latest message only; an answered offer is not shown again. */
const offered = computed(() => {
  const last = messages.value[messages.value.length - 1];
  return { messageId: last?.id || null, options: messageOptions(last) };
});

function showReasoning(message) {
  return message?.Role === 'bot' && (message.Pending || (typeof message.Reasoning === 'string' && message.Reasoning));
}

function toggleReasoning(id) {
  openReasoning.value = { ...openReasoning.value, [id]: !openReasoning.value[id] };
}

function storedMessage(message) {
  const stored = { Role: message.Role, Message: message.Message, Time: message.Time };
  if (message.Widget !== undefined) stored.Widget = message.Widget;
  if (Array.isArray(message.Parts)) stored.Parts = message.Parts;
  if (typeof message.Reasoning === 'string' && message.Reasoning) stored.Reasoning = message.Reasoning;
  if (message.Pending === true) stored.Pending = true;
  if (Array.isArray(message.FileIds) && message.FileIds.length) stored.FileIds = message.FileIds;
  if (Array.isArray(message.Options) && message.Options.length) stored.Options = message.Options;
  if (message.Call && typeof message.Call === 'object') stored.Call = message.Call;
  return stored;
}

function updateDraft(fileId, patch) {
  drafts.value = drafts.value.map((draft) => (draft.fileId === fileId ? { ...draft, ...patch } : draft));
}

function helpChatFilesCallable() {
  return firebase.app().functions().httpsCallable('helpChatFiles', { timeout: 300000 });
}

async function removeDraft(draft) {
  drafts.value = drafts.value.filter((item) => item.fileId !== draft.fileId);
  const databaseURL = appConfig?.firebaseConfig?.databaseURL;
  if (!databaseURL || !props.conversationId) return;
  try {
    await helpChatFilesCallable()({
      action: 'remove',
      conversationId: props.conversationId,
      databaseURL,
      fileId: draft.fileId,
    });
  } catch (error) {
    console.error('[HelpChat] Could not remove file', error);
  }
}

async function onFiles(event) {
  const selected = [...(event.target.files || [])];
  event.target.value = '';
  fileError.value = '';
  if (!selected.length || !props.conversationId) return;
  if (selected.length > MAX_FILES_PER_UPLOAD) {
    fileError.value = `Upload up to ${MAX_FILES_PER_UPLOAD} files at a time.`;
    return;
  }
  const invalid = selected.map((file) => validateClientFile(file)).find(Boolean);
  if (invalid) {
    fileError.value = invalid;
    return;
  }
  const databaseURL = appConfig?.firebaseConfig?.databaseURL;
  if (!databaseURL) {
    fileError.value = 'Database configuration is missing.';
    return;
  }
  try {
    const begun = await helpChatFilesCallable()({
      action: 'begin',
      conversationId: props.conversationId,
      databaseURL,
      files: selected.map((file) => ({
        name: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      })),
    });
    const reserved = begun.data?.files || [];
    drafts.value = [
      ...drafts.value,
      ...reserved.map((item) => ({
        fileId: item.fileId,
        name: item.name,
        status: 'uploading',
        error: '',
        path: item.path,
      })),
    ];
    for (let index = 0; index < reserved.length; index += 1) {
      const item = reserved[index];
      try {
        await firebase.storage().ref(item.path).put(selected[index], {
          contentType: selected[index].type || undefined,
        });
        updateDraft(item.fileId, { status: 'processing' });
      } catch (error) {
        console.error('[HelpChat] Upload failed', error);
        updateDraft(item.fileId, { status: 'failed', error: 'The upload failed.' });
        await helpChatFilesCallable()({
          action: 'remove',
          conversationId: props.conversationId,
          databaseURL,
          fileId: item.fileId,
        }).catch(() => {});
      }
    }
    const uploadedIds = reserved
      .map((item) => item.fileId)
      .filter((fileId) => drafts.value.some((draft) => draft.fileId === fileId && draft.status === 'processing'));
    if (!uploadedIds.length) return;
    const registered = await helpChatFilesCallable()({
      action: 'register',
      conversationId: props.conversationId,
      databaseURL,
      fileIds: uploadedIds,
    });
    for (const item of registered.data?.files || []) {
      updateDraft(item.fileId, { status: item.status, error: item.error || '' });
    }
  } catch (error) {
    console.error('[HelpChat] Could not attach files', error);
    fileError.value = error?.message || 'The file could not be uploaded.';
  }
}

// Format timestamp
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  
  if (isToday) {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } else {
    return date.toLocaleString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }
};

// Scroll to bottom
const scrollToBottom = () => {
  nextTick(() => {
    const container = messagesContainer.value;
    if (!container) return;
    const focus = store.state.helpChatFocus;
    if (focus?.conversationId === props.conversationId && focus.messageId) {
      const target = container.querySelector(`[data-help-message="${CSS.escape(focus.messageId)}"]`);
      if (!target) return;
      target.scrollIntoView({ block: 'nearest' });
      store.commit('save', { helpChatFocus: null });
      return;
    }
    container.scrollTop = container.scrollHeight;
  });
};

function askExample(prompt) {
  if (isWaiting.value || !conversationPath.value) return;
  newMessage.value = prompt;
  sendMessage();
}

/** Send the option's label as the user's message, with the tool call that answers it. */
function chooseOption(option) {
  if (isWaiting.value || !conversationPath.value || filesBlocking.value) return;
  newMessage.value = option.label;
  send(option.call);
}

const sendMessage = () => send();

async function send(call = null) {
  if (!hasMessage.value || !conversationPath.value || isWaiting.value || filesBlocking.value) {
    return;
  }

  try {

    const messageText = newMessage.value.trim();
    const fileIds = drafts.value.filter((draft) => draft.status === 'ready').map((draft) => draft.fileId);
    newMessage.value = '';
    fileError.value = '';

    const currentMessages = messages.value.length > 0
      ? messages.value.map(storedMessage)
      : [];

    const sentAt = new Date().toISOString();
    const userMessage = {
      Role: 'user',
      Message: messageText,
      Time: sentAt,
    };
    if (fileIds.length) userMessage.FileIds = fileIds;
    if (call) userMessage.Call = call;
    const newMessages = [
      ...currentMessages,
      userMessage,
    ];

    const updates = {
      Messages: newMessages,
      UpdatedAt: sentAt,
    };
    if (!currentMessages.some((message) => message.Role === 'user' && message.Message)) {
      updates.Title = plainText(messageText).trim().slice(0, 80);
    }

    await db.update(conversationPath.value, updates);
    drafts.value = [];

    isWaiting.value = true;
    openReasoning.value = {};

    scrollToBottom();

    const databaseURL = appConfig?.firebaseConfig?.databaseURL;
    if (!databaseURL) {
      console.error('[HelpChat] Database configuration is missing');
      isWaiting.value = false;
      return;
    }

    const replied = await requestHelpReply({
      store,
      conversationId: props.conversationId,
      conversationPath: conversationPath.value,
      databaseURL,
      route: helpChatRoute(router.currentRoute.value?.path),
    });
    if (!replied && alive) isWaiting.value = false;
  } catch (error) {
    console.error('[HelpChat] Fatal error in sendMessage:', error);
    if (alive) isWaiting.value = false;
  }
}

// Watch for new messages and scroll
watch(messages, () => {
  scrollToBottom();
  
  if (messages.value.length > 0) {
    const lastMessage = messages.value[messages.value.length - 1];
    if (lastMessage.Role === 'bot' && !lastMessage.Pending) {
      if (isWaiting.value) notifyReply(lastMessage);
      isWaiting.value = false;
    }
  }
}, { deep: true });

watch(conversationFiles, (files) => {
  drafts.value = drafts.value.map((draft) => {
    const remote = files?.[draft.fileId];
    if (!remote) return draft;
    return {
      ...draft,
      name: remote.Name || draft.name,
      status: remote.Status || draft.status,
      error: remote.Error || draft.error,
      path: remote.Path || draft.path,
    };
  });
}, { deep: true });

watch(() => props.conversationId, () => {
  isWaiting.value = false;
  openReasoning.value = {};
  newMessage.value = '';
  drafts.value = [];
  fileError.value = '';
  scrollToBottom();
});
</script>

<style scoped>
.help-start-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.75rem;
  width: 100%;
  margin-top: 1.5rem;
}

.help-start-card {
  background: #fff;
  border: 1px solid #f3f4f6;
  border-radius: 0.75rem;
  padding: 0.75rem 0.75rem 0.35rem;
  text-align: left;
}

.help-example {
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0.35rem 0 0;
  padding: 0.4rem 0.35rem;
  border-radius: 0.5rem;
  background: transparent;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.35;
}

.help-example:hover:not(:disabled) {
  background: #f9fafb;
}

.help-example:disabled {
  background: transparent;
  color: inherit;
  opacity: 0.5;
}

.help-option {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin: 0;
  padding: 0.2rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f9fafb;
  line-height: 1.4rem;
  transition: background-color 0.15s, border-color 0.15s;
}

.help-option:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.help-option:disabled {
  opacity: 0.5;
}

.help-option-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-align: left;
}

.help-bubble {
  max-width: 80%;
  padding: 0.6rem 0.9rem;
  border-radius: 1rem;
  color: #1f2937;
}

.help-bubble-user {
  border-bottom-right-radius: 0.3rem;
  background: #b9cde5;
  color: white;
}

.help-bubble-accent :deep(.help-markdown),
.help-bubble-accent :deep(.help-markdown :not(.help-pill, .help-pill *)) {
  color: white;
}

.help-bubble-bot {
  border: 1px solid #f3f4f6;
  border-bottom-left-radius: 0.3rem;
  background: white;
  box-shadow: 0 1px 3px rgb(0 0 0 / 6%);
}

.help-typing {
  transform: scale(0.6);
  transform-origin: left center;
  height: 24px;
}

.help-typing div {
  top: 6px;
}

.help-reasoning {
  margin-bottom: 0.5rem;
}

.help-reasoning-toggle {
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 0.75rem;
  line-height: 1.2;
  cursor: pointer;
}

.help-reasoning-activity {
  margin: 0.25rem 0 0;
  color: #6b7280;
  font-size: 0.75rem;
}

.help-reasoning-body {
  margin: 0.35rem 0 0;
  max-height: 12rem;
  overflow: auto;
  white-space: pre-wrap;
  color: #4b5563;
  font-family: inherit;
  font-size: 0.75rem;
  line-height: 1.45;
}

.help-reasoning-pulse {
  animation: help-reasoning-pulse 1.4s ease-in-out infinite;
}

@keyframes help-reasoning-pulse {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
}
</style>
